import React, { useState, useRef, useEffect, useMemo } from "react"
import PropTypes from "prop-types"
import Button from "@mui/material/Button"
import Chip from "@mui/material/Chip"
import Tooltip from "@mui/material/Tooltip"
import IconButton from "@mui/material/IconButton"
import ClearIcon from "@mui/icons-material/Clear"
import EditIcon from "@mui/icons-material/Edit"
import RemoveCircleOutlinedIcon from "@mui/icons-material/RemoveCircleOutlined"
import DialogActions from "@mui/material/DialogActions"
import FormControl from "@mui/material/FormControl"
import InputLabel from "@mui/material/InputLabel"
import MenuItem from "@mui/material/MenuItem"
import Select from "@mui/material/Select"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import TextField from "@mui/material/TextField"
import CircularProgress from "@mui/material/CircularProgress"
import RichTextEditor from "components/common/RichTextEditor"
import { verifyResponse } from "utils/httpRequest"
import { Container, Draggable } from "react-smooth-dnd"
import { useSnackbar } from "notistack"
import { useForm } from "react-hook-form"
import { UseConfirmDialog } from "components/customHooks"
import {
  addProgramInnercise,
  updateProgramInnercise,
  addProgramPlaybook,
  editProgramPlaybook,
  deleteProgramPlaybookQuestion,
  getProgramInnerciseById,
  generateInnerciseDescription,
  exchangeChangeProgramPlaybookQuestionIndex,
  getTierPrograms,
} from "services/program"
import { exchangeArrayItem } from "utils/data"
import MediaContentDisplay from "components/common/MediaManager/MediaContentDisplay"
import { StyledInputLabel } from "components/Style/Forms.style"
import { MEDIA_TYPE_INDEX } from "constants/global"
import {
  INNERCISE_MODE,
  PLAYBOOK_RESPONSE_QUESTION_TYPE_INDEX,
} from "constants/global"
import ProgramInnerciseListControl from "./ProgramInnerciseListControl"
import ProgramPlaybookQuestionDialog from "./ProgramPlaybook/ProgramPlaybookQuestionDlg"
import { StyledProgramDialog } from "./Program.style"
import { getExpertByInnercise, listExperts, setInnerciseExpert } from "services/expert"
import { getProducts } from "services/products"
import FormHelperText from "@mui/material/FormHelperText"

const ProgramInnerciseDialog = (props) => {
  const { onSubmit, openMediaManager } = props
  const [thumbnailResource, setThumbnailResource] = useState(false)
  const [resources, setResources] = useState([])
  const [innerciseMode, setInnerciseMode] = useState(
    INNERCISE_MODE.INNERCISE.value
  )
  const thumbnailResourceRef = useRef(null)

  const initInnercise = {
    programId: 0,
    levelId: 0,
    title: "",
    type: 0,
    description: "",
    resourceId: null,
    thumbResourceId: null,
    attachIds: [],
    freeFlag: 0,
  }

  const { enqueueSnackbar } = useSnackbar()

  const [ConfirmDialog, openConfirmDialog] = UseConfirmDialog({
    title: "Delete Question Confirm",
    description: "Are you sure want delete this question ?",
    agreeText: "Yes",
    disagreeText: "No",
  })

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    trigger,
    watch,
    formState: { errors },
  } = useForm()

  const [programId, setProgramId] = useState(0)
  const [programLevelId, setProgramLevelId] = useState(0)
  const [innerciseId, setInnerciseId] = useState()
  const [innercise, setInnercise] = useState(initInnercise)
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const [attachments, setAttachments] = useState([])
  const [isChanged, setIsChanged] = useState(false)
  const [experts, setExperts] = useState([])
  const [selectedExpertId, setSelectedExpertId] = useState("")
  const [tierPrograms, setTierPrograms] = useState([])
  const [products, setProducts] = useState([])
  const [parentAccess, setParentAccess] = useState(null)
  const [generatingDescription, setGeneratingDescription] = useState(false)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    ;(async () => {
      try {
        const tiers = await getTierPrograms()
        if (!cancelled) setTierPrograms(Array.isArray(tiers) ? tiers : [])
      } catch {
        if (!cancelled) setTierPrograms([])
      }
      try {
        const prod = await getProducts()
        if (!cancelled) setProducts(Array.isArray(prod) ? prod : [])
      } catch {
        if (!cancelled) setProducts([])
      }
    })()
    return () => {
      cancelled = true
    }
  }, [open])

  useEffect(() => {
    resources.length === 0
      ? setValue("innerciseResources", null)
      : setValue("innerciseResources", resources)
  }, [resources])

  useEffect(() => {
    if (innerciseId && open) {
      setValue("title", "")
      setValue("description", "")
      refreshInnercise()
    }
  }, [innerciseId, open])

  useEffect(() => {
    if (!open) return
    let cancelled = false

    const loadExpertsAndCurrent = async () => {
      try {
        const expertList = await listExperts()
        const arr = Array.isArray(expertList)
          ? expertList
          : (expertList?.data || [])
        if (!cancelled) setExperts(Array.isArray(arr) ? arr : [])
      } catch (e) {
        if (!cancelled) setExperts([])
      }

      try {
        if (innerciseId) {
          const cur = await getExpertByInnercise(innerciseId)
          const eid = (cur?.expertId ?? cur?.data?.expertId) || null
          if (!cancelled) setSelectedExpertId(eid ? String(eid) : "")
        } else {
          if (!cancelled) setSelectedExpertId("")
        }
      } catch (e) {
        if (!cancelled) setSelectedExpertId("")
      }
    }

    loadExpertsAndCurrent()
    return () => {
      cancelled = true
    }
  }, [open, innerciseId])

  useEffect(() => {
    if (isEditMode && innerciseMode === INNERCISE_MODE.INNERCISE.value) {
      // set thumbnail image
      thumbnailResourceRef.current = innercise.thumbResourceDto
      setValue("thumbResourceId", innercise.thumbResourceDto?.id || null)
      trigger("thumbResourceId") // Trigger validation after setting value

      // set basic info
      setValue("title", innercise.title)
      setValue("description", innercise.description)
      setValue("tierProgramId", innercise?.tierProgramId ?? "")
      setValue("productProgramId", innercise?.productProgramId ?? "")

      // set attachment
      innercise.attachDtos
        ? setAttachments(innercise.attachDtos)
        : setAttachments([])

      const currentResources = innercise.resourceDtos?.map((resource) => {
        return {
          channelType: resource.channelType,
          resource,
        }
      }) || []

      // set innercise resources
      setTimeout(() => {
        setValue("innerciseResources", currentResources)
      }, 200)
      setResources(currentResources)
    } else if (isEditMode && innerciseMode === INNERCISE_MODE.PLAYBOOK.value) {
      thumbnailResourceRef.current = innercise.thumbResourceDto
      setValue("thumbResourceId", innercise.thumbResourceDto?.id || null)
      trigger("thumbResourceId") // Trigger validation after setting value

      // set basic info
      setValue("title", innercise.title)
      setValue("description", innercise.description)
      setValue("tierProgramId", innercise?.tierProgramId ?? "")
      setValue("productProgramId", innercise?.productProgramId ?? "")

      setAttachments([])
      setResources([])
      const currentResources = []
      setTimeout(() => {
        setValue("innerciseResources", currentResources)
      }, 200)
      setResources(currentResources)
    } else {
      thumbnailResourceRef.current = null
      setAttachments([])
      setResources([])
      setValue("innerciseResources", null)
      setValue("tierProgramId", "")
      setValue("productProgramId", "")
    }

    setTimeout(() => {
      updateThumbnailResource()
    }, 0)
  }, [innercise.id, innercise.title, innercise.description, innercise.thumbResourceDto, innercise.attachDtos, innercise.resourceDtos, isEditMode, innerciseMode])

  const refreshInnercise = async () => {
    setLoading(true)
    const response = await getProgramInnerciseById(innerciseId)
    setLoading(false)
    verifyResponse(response)

    if (response.rtnCode === 200) {
      setInnercise(response.data)
    } else {
      //
    }
  }

  const [
    openPlaybookQuestionDlg,
    playbookQuestionDlg,
    playbookQuestionDlgLoading,
  ] = ProgramPlaybookQuestionDialog({
    refreshPlaybook: refreshInnercise,
  })

  const openDlg = (innerciseMode, programId, levelId, innerciseId, parentAccessArg) => {
    reset()
    setInnerciseMode(innerciseMode)
    setProgramId(programId)
    setProgramLevelId(levelId)
    setParentAccess(parentAccessArg || null)

    if (innerciseId) {
      setIsEditMode(true)
      setInnerciseId(innerciseId)
    } else {
      setIsEditMode(false)
      setInnerciseId(undefined)
      setSelectedExpertId("")
      setInnercise({
        ...initInnercise,
        programId: programId,
        levelId: levelId,
      })
      // Initialize thumbResourceId for new innercise
      setValue("thumbResourceId", null)
      setValue("tierProgramId", "")
      setValue("productProgramId", "")
      trigger("thumbResourceId") // Trigger validation after initializing value
    }

    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  const updateThumbnailResource = () => {
    setThumbnailResource({
      ...thumbnailResourceRef.current,
      selectAction: selectThumbnailResource,
      clearResourceAction: clearThumbnailResource,
    })
  }

  const selectThumbnailResource = () => {
    openMediaManager(
      (media) => {
        thumbnailResourceRef.current = media
        setValue("thumbResourceId", media.id)
        trigger("thumbResourceId") // Trigger validation after setting value
        updateThumbnailResource()
      },
      [MEDIA_TYPE_INDEX.image]
    )
  }

  const clearThumbnailResource = () => {
    thumbnailResourceRef.current = null
    setValue("thumbResourceId", null)
    trigger("thumbResourceId") // Trigger validation after clearing value
    updateThumbnailResource()
  }

  const removeAttachment = (attachId) => {
    setAttachments(attachments.filter((attach) => attach.id !== attachId))
  }

  const handleGenerateDescription = async () => {
    if (resources.length === 0) {
      enqueueSnackbar("Select a media resource before generating a description.", {
        variant: "error",
      })
      return
    }
    if (!isEditMode || !innercise?.id) {
      enqueueSnackbar(
        "Save this innercise with its media first, then generate a description once the transcript is ready.",
        { variant: "error" }
      )
      return
    }

    setGeneratingDescription(true)
    try {
      const resp = await generateInnerciseDescription({
        innerciseId: innercise.id,
        resourceId: resources[0].resource.id,
      })
      verifyResponse(resp)

      if (resp.data.rtnCode === 200) {
        setValue("description", resp.data.data.description)
        setInnercise((prev) => ({ ...prev, description: resp.data.data.description }))
      } else {
        enqueueSnackbar(resp.data.message || "Failed to generate description.", {
          variant: "error",
        })
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message || error?.message || "Failed to generate description."
      enqueueSnackbar(errorMessage, { variant: "error" })
    } finally {
      setGeneratingDescription(false)
    }
  }

  const removePlaybookQuestion = async (id) => {
    setLoading(true)
    const response = await deleteProgramPlaybookQuestion({ id })
    setLoading(false)
    verifyResponse(response)

    if (response.rtnCode === 200) {
      // handling success message
      enqueueSnackbar("Playbook Question Deleted", {
        variant: "success",
      })
      // refresh
      refreshInnercise()
    } else {
      // handling success message
      enqueueSnackbar("Delete Playbook Question Fail", {
        variant: "warning",
      })
    }
  }

  const handleAction = async (data) => {
    setLoading(true)

    try {
      let response
      const tierProgramId =
        data?.tierProgramId === "" || typeof data?.tierProgramId === "undefined"
          ? null
          : Number(data.tierProgramId)
      const productProgramId =
        data?.productProgramId === "" || typeof data?.productProgramId === "undefined"
          ? null
          : Number(data.productProgramId)

      // Validation: if Tier override is the special "product" type, a Product Gate must exist
      // either on this lesson override or inherited from the parent access (Level/Program).
      try {
        const selectedTier = tierPrograms.find((t) => Number(t?.id) === Number(tierProgramId))
        const isProductTier =
          selectedTier && String(selectedTier.type).toLowerCase() === "product"
        const effectiveProductProgramId =
          productProgramId ?? (parentAccess?.resolvedProductProgramId ?? null)
        if (isProductTier && !effectiveProductProgramId) {
          enqueueSnackbar(
            "Product Gate is required when Tier Program is set to Product (set it here, on the Level, or on the Program).",
            { variant: "error" }
          )
          setLoading(false)
          return
        }
      } catch {
        // ignore
      }

      if (innerciseMode === INNERCISE_MODE.INNERCISE.value) {
        if (isEditMode) {
          // update innercise
          response = await updateProgramInnercise({
            ...data,
            type: resources.length === 0 ? 0 : resources[0].resource.type,
            id: innercise.id,
            programId: programId,
            levelId: programLevelId,
            freeFlag: 0,
            tierProgramId,
            productProgramId,
            attachIds: attachments.map((attach) => attach.id),
            innerciseResourceList: resources.map((chResource) => {
              return {
                channelType: chResource.channelType,
                resourceId: parseInt(chResource.resource.id),
              }
            }),
          })
        } else {
          // create innercise
          response = await addProgramInnercise({
            ...data,
            type: resources.length === 0 ? 0 : resources[0].resource.type,
            programId: programId,
            levelId: programLevelId,
            freeFlag: 0,
            tierProgramId,
            productProgramId,
            attachIds: attachments.map((attach) => attach.id),
            innerciseResourceList: resources.map((chResource) => {
              return {
                channelType: chResource.channelType,
                resourceId: parseInt(chResource.resource.id),
              }
            }),
          })
        }
      }

      if (innerciseMode === INNERCISE_MODE.PLAYBOOK.value) {
        if (isEditMode) {
          // update playbook
          response = await editProgramPlaybook({
            ...data,
            id: innercise.id,
            programId: programId,
            levelId: programLevelId,
            estTimeLength: 234,
          })

          // update question index if there are changed
          if (isChanged) {
            const exchangeResponse =
              await exchangeChangeProgramPlaybookQuestionIndex({
                updateIndexArray: innercise.questionDtoList.map((question) => {
                  return { id: question.id, index: question.dataIndex }
                }),
              })

            verifyResponse(exchangeResponse)
          }
        } else {
          // create playbook
          response = await addProgramPlaybook({
            ...data,
            programId: programId,
            levelId: programLevelId,
            estTimeLength: 234,
          })
        }
      }

      verifyResponse(response)

      if (response.data.rtnCode === 200) {
        // Persist optional expert association for this lesson (guest expert)
        try {
          const savedInnerciseId = isEditMode
            ? innercise.id
            : (response?.data?.data?.id || response?.data?.data?.data?.id)

          if (savedInnerciseId) {
            await setInnerciseExpert(
              Number(savedInnerciseId),
              selectedExpertId ? Number(selectedExpertId) : null
            )
          }
        } catch (e) {
          // Non-fatal: lesson saved, expert association failed
        }

        // handling success message
        enqueueSnackbar(
          isEditMode ? "Innercise Updated" : "Create Innercise Success",
          {
            variant: "success",
          }
        )
      } else {
        // handling error message - display API error message
        enqueueSnackbar(
          response.data.message || (isEditMode ? "Innercise Update Fail" : "Create Innercise Fail"),
          {
            variant: "error",
          }
        )
      }

      onSubmit()
      setOpen(false)
    } catch (error) {
      // Handle network errors or other exceptions
      const errorMessage = error?.response?.data?.message || 
                          error?.message || 
                          (isEditMode ? "Innercise Update Fail" : "Create Innercise Fail")
      
      enqueueSnackbar(errorMessage, {
        variant: "error",
      })
    } finally {
      setLoading(false)
    }
  }

  const dlg = useMemo(() => {
    return (
      <StyledProgramDialog open={open} fullWidth={true} onClose={handleClose}>
        <DialogTitle>
          <div className="dialog-title-group">
            <h2 className="dialog-title">
              {innerciseMode === INNERCISE_MODE.INNERCISE.value &&
                (isEditMode ? "Edit Innercise" : "Add Innercise")}
              {innerciseMode === INNERCISE_MODE.PLAYBOOK.value &&
                (isEditMode ? "Edit Playbook" : "Add Playbook")}
            </h2>
            <p className="dialog-subtitle">
              Configure lesson content, media resources, and optional access
              overrides.
            </p>
          </div>
        </DialogTitle>
        <form onSubmit={handleSubmit(handleAction)}>
          <DialogContent>
            {innerciseMode === INNERCISE_MODE.INNERCISE.value && (
              <div className="form-control-row ">
                <ProgramInnerciseListControl
                  openMediaManager={openMediaManager}
                  openPlaybookDlg={() => {
                    openPlaybookQuestionDlg({ playbookId: innercise.id })
                  }}
                  resources={resources}
                  setResources={setResources}
                  innerciseMode={innerciseMode}
                />
                <input
                  type="hidden"
                  {...register("innerciseResources", { required: true })}
                />
                {!!errors?.innerciseResources && (
                  <div className="error-message">
                    * Innercise Resource Required
                  </div>
                )}
              </div>
            )}

            <FormControl fullWidth>
              <TextField
                autoFocus
                focused
                autoComplete="off"
                margin="dense"
                id="program-innercise-title"
                label="Title"
                fullWidth
                defaultValue={innercise.title}
                {...register("title", { required: true })}
                error={!!errors?.title}
                variant="standard"
              />
            </FormControl>

            <FormControl fullWidth>
              {innerciseMode === INNERCISE_MODE.INNERCISE.value && (
                <div className="form-control-row" style={{ marginBottom: 8 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={generatingDescription}
                    onClick={handleGenerateDescription}
                    startIcon={generatingDescription ? <CircularProgress size={16} /> : null}
                  >
                    {generatingDescription ? "Generating..." : "Generate Description from Transcript"}
                  </Button>
                </div>
              )}
              <RichTextEditor
                value={watch("description") || innercise.description || ''}
                onChange={(value) => {
                  setValue("description", value)
                  setInnercise({ ...innercise, description: value })
                }}
                label="Description"
                placeholder="Enter innercise description..."
                toolbarConfig="description"
                minHeight="150px"
                error={!!errors?.description}
                helperText={!!errors?.description ? "* Description is required" : ""}
              />
            </FormControl>

            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel id="innercise-expert-select-label">Guest Expert</InputLabel>
              <Select
                fullWidth
                labelId="innercise-expert-select-label"
                id="innercise-expert-select"
                value={selectedExpertId || ""}
                label="Guest Expert"
                onChange={(e) => setSelectedExpertId(e.target.value)}
              >
                <MenuItem value="">Inherit from Program</MenuItem>
                {experts.map((ex) => (
                  <MenuItem key={ex.id} value={String(ex.id)}>
                    {ex.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Tier / Product Access (optional override; otherwise inherit from Level → Program) */}
            <input type="hidden" {...register("tierProgramId")} />
            <input type="hidden" {...register("productProgramId")} />
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel id="innercise-tier-program-select-label">Tier Program</InputLabel>
              <Select
                fullWidth
                labelId="innercise-tier-program-select-label"
                id="innercise-tier-program-select"
                label="Tier Program"
                value={watch("tierProgramId") ?? ""}
                onChange={(e) => setValue("tierProgramId", e.target.value)}
              >
                <MenuItem value="">
                  {(() => {
                    const resolvedTierId = parentAccess?.resolvedTierProgramId ?? null
                    const via = parentAccess?.levelTierProgramId ? "Level" : "Program"
                    const tier = tierPrograms.find((t) => Number(t.id) === Number(resolvedTierId))
                    const tierType = tier?.type || "default"
                    return `Inherit from ${via} (${tierType})`
                  })()}
                </MenuItem>
                {[...tierPrograms]
                  .sort((a, b) => {
                    const order = { Free: 0, Silver: 1, Gold: 2, Platinum: 3, product: 4 }
                    const aIdx = order[a?.type] ?? 99
                    const bIdx = order[b?.type] ?? 99
                    return aIdx - bIdx
                  })
                  .map((tier) => (
                    <MenuItem key={tier.id} value={tier.id}>
                      {String(tier.type).toLowerCase() === "product"
                        ? "Product (select below)"
                        : tier.type}
                    </MenuItem>
                  ))}
              </Select>
              <FormHelperText>
                Leave this as “Inherit from Level” to use the Level’s access (and if the Level inherits, it will use the Program’s access).
              </FormHelperText>
            </FormControl>

                <FormControl fullWidth sx={{ mt: 1 }}>
              <InputLabel id="innercise-product-program-select-label">Product Gate</InputLabel>
                  <Select
                    fullWidth
                    labelId="innercise-product-program-select-label"
                    id="innercise-product-program-select"
                label="Product Gate"
                    value={watch("productProgramId") ?? ""}
                    onChange={(e) => setValue("productProgramId", e.target.value)}
                  >
                <MenuItem value="">
                  {(() => {
                    const via = parentAccess?.levelProductProgramId ? "Level" : "Program"
                    const prod = products.find(
                      (p) => Number(p.programId) === Number(parentAccess?.resolvedProductProgramId)
                    )
                    const prodLabel = prod?.name || "None"
                    return `Inherit from ${via} (${prodLabel})`
                  })()}
                </MenuItem>
                    {products.map((p) => (
                      <MenuItem key={p.programId} value={p.programId}>
                        {p.name}
                      </MenuItem>
                    ))}
                  </Select>
              <FormHelperText>
                Optional. Set this if you want this lesson to be accessible by a Product Gate in addition to (or instead of) the Tier.
              </FormHelperText>
                </FormControl>

            <div className="form-control-row two-col">
              <div>
                <StyledInputLabel>Thumbnail</StyledInputLabel>
                <input
                  type="hidden"
                  {...register("thumbResourceId", { required: true })}
                />
                <MediaContentDisplay resource={thumbnailResource} />
                {!!errors?.thumbResourceId && (
                  <div className="error-message">
                    * Thumbnail Resource Required
                  </div>
                )}
              </div>
            </div>

            {isEditMode && innerciseMode === INNERCISE_MODE.PLAYBOOK.value && (
              <div className="questions">
                <div className="action-bar">
                  <Button
                    variant="contained"
                    onClick={() => {
                      // add new question
                      openPlaybookQuestionDlg({ playbookId: innercise.id })
                    }}
                  >
                    New Question
                  </Button>
                </div>

                {innercise.questionDtoList && (
                  <div className="question-list">
                    <Container
                      onDrop={async (e) => {
                        setIsChanged(true)
                        const updateIndexArray = exchangeArrayItem({
                          addedIndex: e.addedIndex,
                          removedIndex: e.removedIndex,
                          indexString: "dataIndex",
                          list: innercise.questionDtoList,
                        })
                        setInnercise({
                          ...innercise,
                          questionDtoList: updateIndexArray,
                        })
                      }}
                    >
                      {innercise.questionDtoList.map((question) => (
                        <Draggable key={question.id}>
                          <div
                            className="question"
                            key={`playbook-question-item-${question.id}`}
                          >
                            <div className="title">
                              <div className="content">
                                <Chip
                                  label={
                                    PLAYBOOK_RESPONSE_QUESTION_TYPE_INDEX[
                                      question.answerType
                                    ]
                                  }
                                  color="primary"
                                  size="small"
                                />
                                {question.title}
                              </div>
                              <div className="actions">
                                <Tooltip title="edit">
                                  <IconButton
                                    aria-label="edit playbook question title"
                                    sx={{ color: "#bf851a" }}
                                    component="label"
                                    onClick={() => {
                                      openPlaybookQuestionDlg({
                                        playbookId: innercise.id,
                                        playbookQuestion: question,
                                      })
                                    }}
                                  >
                                    <EditIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Remove">
                                  <IconButton
                                    aria-label="remove playbook question"
                                    sx={{ color: "#bf851a" }}
                                    component="label"
                                    onClick={() => {
                                      openConfirmDialog(() => {
                                        removePlaybookQuestion(question.id)
                                      })
                                    }}
                                  >
                                    <ClearIcon />
                                  </IconButton>
                                </Tooltip>
                              </div>
                            </div>
                            {question.answerList &&
                              question.answerList.length > 0 && (
                                <ol>
                                  {question.answerList.map((answer) => (
                                    <li
                                      key={`playbook-question-answer-${question.id}-${answer.dataIndex}`}
                                    >
                                      {answer.title}{" "}
                                      {answer.otherFlag ? "(other)" : ""}
                                    </li>
                                  ))}
                                </ol>
                              )}
                          </div>
                        </Draggable>
                      ))}
                    </Container>
                  </div>
                )}
              </div>
            )}

            {innerciseMode === INNERCISE_MODE.INNERCISE.value && (
              <>
                <div className="form-control-row">
                  <div className="attachments-section">
                    <StyledInputLabel>Attachments</StyledInputLabel>
                    <Button
                      onClick={() => {
                        openMediaManager(
                          (media) => {
                            setAttachments([...attachments, media])
                          },
                          [MEDIA_TYPE_INDEX.document, MEDIA_TYPE_INDEX.image]
                        )
                      }}
                    >
                      Add Attachment
                    </Button>
                    <div className="attachment-list">
                      {attachments.length === 0
                        ? "No Attachments ..."
                        : attachments.map((attachment) => (
                            <div
                              className="attachment-item"
                              key={`attachment-item-${attachment.id}`}
                            >
                              <MediaContentDisplay
                                resource={attachment}
                                small
                              />
                              <div className="info">{attachment.name}</div>
                              <IconButton
                                className="attachment-close-btn"
                                aria-label="delete"
                                size="small"
                                color="success"
                              >
                                <RemoveCircleOutlinedIcon
                                  fontSize="small"
                                  onClick={() => {
                                    removeAttachment(attachment.id)
                                  }}
                                />
                              </IconButton>
                            </div>
                          ))}
                    </div>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              className="dialog-cancel-btn"
              autoFocus
              variant="outlined"
              size="medium"
              onClick={handleClose}
            >
              Close
            </Button>
            <Button
              className="dialog-submit-btn"
              type="submit"
              variant="contained"
              size="medium"
              autoFocus
            >
              {isEditMode ? "Update" : "Create"}
            </Button>
          </DialogActions>
        </form>
        {playbookQuestionDlg}
        {ConfirmDialog}
      </StyledProgramDialog>
    )
  }, [open, thumbnailResource, innercise, playbookQuestionDlg, experts, selectedExpertId, resources, generatingDescription])

  return [openDlg, dlg, loading || playbookQuestionDlgLoading]
}

ProgramInnerciseDialog.propTypes = {
  onSubmit: PropTypes.func,
  openMediaManager: PropTypes.func.isRequired,
}

ProgramInnerciseDialog.defaultProps = {
  onSubmit: null,
}

export default ProgramInnerciseDialog
